// Shared TSPL label command generator for TSC label printers.
// Extracted so multiple reprint surfaces (FG USN Reprint, Sales Return, etc.)
// render identical stickers. Mirrors the generator used in FGUSNLabelReprint.jsx.

export const PRINTER_CONFIGS = {
  TSC_TE210: { name: 'TSC TE210 (Desktop)', dpi: 203, speed: 4, density: 8, vendorId: 0x1203 },
  TSC_MH240: { name: 'TSC MH240 (Industrial)', dpi: 203, speed: 6, density: 10, vendorId: 0x1203 },
};

// True when a template carries a real coordinate layout (positioned fields).
// A field-less template (fields:{}) — or a barely-configured one — returns
// false; rendering such a template would silently fall back to cramped
// product-default coordinates. Used to guard box-sticker printing so a
// mis-configured box template never prints an overlapping/crammed label.
// Requires a QUORUM of the key box fields to be positioned (not just one),
// so a partially-configured template can't slip through and cram the rest.
export function templateHasLayout(template) {
  const f = template && template.fields;
  if (!f || typeof f !== 'object') return false;
  const keys = [
    'quality_label', 'quality_value', 'size_label', 'size_value',
    'mrp_label', 'mrp_value', 'qty', 'qr1', 'qr2', 'made_in_india',
  ];
  const positioned = keys.filter((k) => {
    const fld = f[k];
    return fld && typeof fld.x === 'number' && typeof fld.y === 'number' && (fld.x !== 0 || fld.y !== 0);
  }).length;
  return positioned >= 5;
}

// Generate native TSPL commands for one sticker from resolved label data +
// template field positions. `opts` = { displayUsn, printerConfig, templateType,
// arialLineSpacingMultiplier }.
export function generateTsplFromLabelData(labelData, template, usnCode, mrpFormatted, opts = {}) {
  const {
    displayUsn = null,
    printerConfig = null,
    templateType = null,
    arialLineSpacingMultiplier = 2.5,
  } = opts;

  const config = printerConfig || PRINTER_CONFIGS.TSC_TE210;
  const widthMm = template ? Math.round(template.label_width_dots / 8) : 100;
  const heightMm = template ? Math.round(template.label_height_dots / 8) : 80;
  const usnDisplay = displayUsn || usnCode;
  const fields = template?.fields || {};
  const tplType = templateType || template?.template_type || '';

  const getField = (key, defaultX = 0, defaultY = 0, defaultFontSize = 2, defaultSize = 4) => {
    const field = fields[key] || {};
    return {
      x: field.x ?? defaultX,
      y: field.y ?? defaultY,
      font_size: field.font_size ?? defaultFontSize,
      bold: field.bold ?? false,
      size: field.size ?? defaultSize,
      text: field.text || '',
    };
  };

  const generateTextCmd = (x, y, fontSize, text) => {
    const arialSize = fontSize || 10.5;
    return `TEXT ${x},${y},"ARIALB.TTF",0,${arialSize},${arialSize},"${text}"\r\n`;
  };

  let tspl = '';
  tspl += `SIZE ${widthMm} mm, ${heightMm} mm\r\n`;
  tspl += `GAP 3 mm, 0 mm\r\n`;
  tspl += `SPEED ${config.speed}\r\n`;
  tspl += `DENSITY ${config.density}\r\n`;
  tspl += `DIRECTION 1,0\r\n`;
  tspl += `REFERENCE 0,0\r\n`;
  tspl += `OFFSET 0 mm\r\n`;
  tspl += `SET PEEL OFF\r\n`;
  tspl += `SET CUTTER OFF\r\n`;
  tspl += `SET PARTIAL_CUTTER OFF\r\n`;
  tspl += `CLS\r\n`;

  // QUALITY
  const qualityLabel = getField('quality_label', 16, 16, 10.5);
  const qualityValue = getField('quality_value', 160, 16, 14.0);
  if (labelData.quality) {
    tspl += generateTextCmd(qualityLabel.x, qualityLabel.y, qualityLabel.font_size, qualityLabel.text || 'QUALITY');
    tspl += generateTextCmd(qualityValue.x, qualityValue.y, qualityValue.font_size, labelData.quality);
  }

  // COLOR
  if (labelData.color) {
    const colorLabel = getField('color_label', 16, 56, 10.5);
    const colorValue = getField('color_value', 160, 56, 10.5);
    tspl += generateTextCmd(colorLabel.x, colorLabel.y, colorLabel.font_size, colorLabel.text || 'COLOR');
    tspl += generateTextCmd(colorValue.x, colorValue.y, colorValue.font_size, labelData.color);
  }

  // CONTENT
  const contentLabel = getField('content_label', 16, 96, 10.5);
  const contentValue = getField('content_value', 160, 96, 14.0);
  if (labelData.content1) {
    const contentParts = [labelData.content1];
    if (labelData.content2) contentParts.push(labelData.content2);
    if (labelData.content3) contentParts.push(labelData.content3);
    const content = contentParts.join('    ');
    tspl += generateTextCmd(contentLabel.x, contentLabel.y, contentLabel.font_size, contentLabel.text || 'CONTENT');
    tspl += generateTextCmd(contentValue.x, contentValue.y, contentValue.font_size, content);
  }

  // SIZE
  const sizeLabel = getField('size_label', 16, 136, 10.5);
  const sizeValue = getField('size_value', 160, 136, 10.5);
  const sizeParts = [];
  if (labelData.size1) sizeParts.push(labelData.size1);
  if (labelData.size2) sizeParts.push(labelData.size2);
  if (labelData.size3) sizeParts.push(labelData.size3);
  const size = sizeParts.length > 0 ? sizeParts.join('    ') : (labelData.size_from_rates || '');
  if (size) {
    tspl += generateTextCmd(sizeLabel.x, sizeLabel.y, sizeLabel.font_size, sizeLabel.text || 'SIZE');
    tspl += generateTextCmd(sizeValue.x, sizeValue.y, sizeValue.font_size, size);
  }

  // DESC5 Bold for combo
  if (labelData.desc5_bold && tplType?.includes('combo')) {
    const desc5Field = getField('desc5_bold', 400, 56, 14.0);
    tspl += generateTextCmd(desc5Field.x, desc5Field.y, desc5Field.font_size, labelData.desc5_bold);
  }

  // QTY
  const qtyField = getField('qty', 16, 176, 10.5);
  const qtyVal = template?.fields?.qty?.value;
  const qtyText = (qtyVal !== undefined && qtyVal !== null && qtyVal !== '')
    ? `QTY : ${qtyVal} N`
    : (labelData.qty || 'QTY : 1 N');
  tspl += generateTextCmd(qtyField.x, qtyField.y, qtyField.font_size, qtyText);

  // MRP
  const mrpLabel = getField('mrp_label', 200, 176, 10.5);
  const mrpValue = getField('mrp_value', 280, 166, 18.0);
  tspl += generateTextCmd(mrpLabel.x, mrpLabel.y, mrpLabel.font_size, mrpLabel.text || 'MRP :');
  tspl += generateTextCmd(mrpValue.x, mrpValue.y, mrpValue.font_size, mrpFormatted);

  // WSP (wholesale price) — renders only when the template positions the WSP
  // field AND the rate carries a WSP value. Keeps existing labels untouched.
  const wspValue = getField('wsp_value', 0, 0, 18.0);
  const wspText = labelData.wsp_formatted
    || (labelData.wsp ? `Rs.${Number(labelData.wsp).toFixed(2)}` : '');
  if ((wspValue.x || wspValue.y) && wspText) {
    const wspLabel = getField('wsp_label', 0, 0, 10.5);
    if (wspLabel.x || wspLabel.y) {
      tspl += generateTextCmd(wspLabel.x, wspLabel.y, wspLabel.font_size, wspLabel.text || 'WSP :');
    }
    tspl += generateTextCmd(wspValue.x, wspValue.y, wspValue.font_size, wspText);
  }
  const taxNotice = getField('tax_notice', 280, 210, 7.5);
  tspl += generateTextCmd(taxNotice.x, taxNotice.y, taxNotice.font_size, '(INCL. OF ALL TAXES)');

  // MADE IN INDIA
  const madeInIndia = getField('made_in_india', 16, 250, 10.5);
  tspl += generateTextCmd(madeInIndia.x, madeInIndia.y, madeInIndia.font_size, 'MADE IN INDIA');

  // QR Code 1 (USN)
  const qr1 = getField('qr1', 16, 290, 10.5, 5);
  const qr1Text = getField('qr1_text', 16, 410, 10.5);
  if (usnCode) {
    tspl += `QRCODE ${qr1.x},${qr1.y},L,${qr1.size},A,0,"${usnCode}"\r\n`;
    tspl += generateTextCmd(qr1Text.x, qr1Text.y, qr1Text.font_size, usnDisplay);
  }

  // QR Code 2 (Barcode)
  const qr2 = getField('qr2', 200, 290, 10.5, 5);
  const qr2Text = getField('qr2_text', 200, 410, 10.5);
  if (labelData.qr2_code) {
    tspl += `QRCODE ${qr2.x},${qr2.y},L,${qr2.size},A,0,"${labelData.qr2_code}"\r\n`;
    tspl += generateTextCmd(qr2Text.x, qr2Text.y, qr2Text.font_size, labelData.qr2_code);
  }

  // Consumer Notice
  const consumerNotice = getField('consumer_notice', 16, 460, 7.5);
  const actualFontSize = consumerNotice.font_size || 7.5;
  const lineHeight = Math.ceil(actualFontSize * arialLineSpacingMultiplier);
  tspl += generateTextCmd(consumerNotice.x, consumerNotice.y, consumerNotice.font_size, 'Note: Consumer is free to open and');
  tspl += generateTextCmd(consumerNotice.x, consumerNotice.y + lineHeight, consumerNotice.font_size, 'inspect the product before buying it');

  tspl += `PRINT 1,1\r\n`;
  return tspl;
}
